import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneCall, PhoneOff, Video, Mic, Swords } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL, errText } from "@/lib/api";
import { CoinIcon } from "@/components/Icons";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";

const CallContext = createContext(null);

export function CallProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [incoming, setIncoming] = useState(null);
  const [outgoing, setOutgoing] = useState(null);
  const [tick, setTick] = useState(null);
  const [lowBalance, setLowBalance] = useState(false);
  const [endedSummary, setEndedSummary] = useState(null);
  const [pkChallenge, setPkChallenge] = useState(null);
  const wsRef = useRef(null);
  const outgoingRef = useRef(null);
  outgoingRef.current = outgoing;

  useEffect(() => {
    if (!user?.id) return;
    let closed = false;
    let ws;
    let es;

    const handleMessage = (msg) => {
      switch (msg.type) {
        case "incoming_call":
          setIncoming(msg.call);
          break;
        case "call_accepted":
          setOutgoing(null);
          navigate(`/call/${msg.call_id}`);
          break;
        case "call_tick":
          setTick(msg);
          break;
        case "call_low_balance":
          setLowBalance(true);
          setTimeout(() => setLowBalance(false), 28000);
          break;
        case "call_ended": {
          setIncoming((i) => (i?.id === msg.summary.call_id ? null : i));
          if (outgoingRef.current?.call?.id === msg.summary.call_id) {
            setOutgoing(null);
            const r = msg.summary.reason;
            if (r === "rejected") toast.error("Call declined");
            else if (r === "missed") toast.error("No answer");
          }
          setEndedSummary(msg.summary);
          setLowBalance(false);
          break;
        }
        case "pk_challenge":
          setPkChallenge(msg.battle);
          break;
        case "pk_declined":
          toast.error(msg.text || "PK challenge declined");
          break;
        default:
          break;
      }
    };

    const connect = () => {
      const token = localStorage.getItem("yaara_token");
      if (!token) return;

      const sseUrl = `${BACKEND_URL}/ws/user?token=${token}&sse=1`;
      const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws/user?token=${token}`;
      console.log("[Yaara Live] WEBSOCKET URL:", wsUrl);
      console.log("[Yaara Live] SSE STREAM URL:", sseUrl);

      // 1. Try SSE EventSource first (works 100% through Apache reverse proxy)
      try {
        es = new EventSource(sseUrl);
        es.onmessage = (ev) => {
          let msg;
          try { msg = JSON.parse(ev.data); } catch { return; }
          handleMessage(msg);
        };
        es.onerror = () => {
          if (!closed && es) {
            es.close();
            setTimeout(() => { if (!closed) connectWS(); }, 3000);
          }
        };
      } catch (e) {
        connectWS();
      }
    };

    const connectWS = () => {
      const token = localStorage.getItem("yaara_token");
      if (!token) return;
      try {
        const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws/user?token=${token}`;
        console.log("[Yaara Live] CONNECTING WEBSOCKET:", wsUrl);
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          let msg;
          try { msg = JSON.parse(ev.data); } catch { return; }
          handleMessage(msg);
        };
        ws.onclose = () => {
          if (!closed) setTimeout(connect, 3000);
        };
      } catch (e) {}
    };

    // 2. Short-poll fallback for incoming calls (guarantees call popup on 100% of networks & devices)
    const pollInterval = setInterval(async () => {
      if (closed) return;
      try {
        const { data } = await api.get("/calls/incoming/check");
        if (data?.incoming) {
          setIncoming(data.incoming);
        }
      } catch (e) {}
    }, 1500);

    connect();
    return () => {
      closed = true;
      clearInterval(pollInterval);
      es?.close();
      ws?.close();
    };
  }, [user?.id, navigate]);

  // Fail-safe status check for outgoing call acceptance
  useEffect(() => {
    if (!outgoing?.call?.id) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/calls/${outgoing.call.id}`);
        if (data?.call?.status === "active") {
          setOutgoing(null);
          navigate(`/call/${outgoing.call.id}`);
        } else if (data?.call?.status && data.call.status !== "ringing") {
          setOutgoing(null);
          const r = data.call.end_reason || data.call.status;
          if (r === "rejected") toast.error("Call declined");
          else if (r === "missed") toast.error("No answer");
        }
      } catch (e) {}
    }, 1500);
    return () => clearInterval(interval);
  }, [outgoing, navigate]);

  const requestCall = useCallback(async (callee, mode) => {
    try {
      const { data } = await api.post("/calls/request", { callee_id: callee.id, mode });
      setEndedSummary(null);
      setOutgoing({ call: data.call, callee: data.callee });
    } catch (e) {
      toast.error(errText(e));
    }
  }, []);

  const cancelOutgoing = async () => {
    if (!outgoing) return;
    try { await api.post(`/calls/${outgoing.call.id}/cancel`); } catch (e) { console.warn("Cancel-call request failed", e); }
    setOutgoing(null);
  };

  const acceptIncoming = async () => {
    if (!incoming) return;
    const id = incoming.id;
    try {
      await api.post(`/calls/${id}/accept`);
      setIncoming(null);
      setEndedSummary(null);
      navigate(`/call/${id}`);
    } catch (e) {
      toast.error(errText(e));
      setIncoming(null);
    }
  };

  const rejectIncoming = async () => {
    if (!incoming) return;
    try { await api.post(`/calls/${incoming.id}/reject`); } catch (e) { console.warn("Reject-call request failed", e); }
    setIncoming(null);
  };

  const acceptPk = async () => {
    if (!pkChallenge) return;
    try {
      await api.post(`/pk/${pkChallenge.id}/accept`);
      toast.success("PK battle started! ⚔️");
    } catch (e) {
      toast.error(errText(e));
    }
    setPkChallenge(null);
  };

  const rejectPk = async () => {
    if (!pkChallenge) return;
    try { await api.post(`/pk/${pkChallenge.id}/reject`); } catch {}
    setPkChallenge(null);
  };

  return (
    <CallContext.Provider value={{ requestCall, tick, lowBalance, endedSummary, setEndedSummary }}>
      {children}

      {incoming && (
        <div className="fixed inset-0 z-[11000] bg-black/85 flex items-center justify-center" data-testid="incoming-call-modal">
          <div className="max-w-md w-full mx-6 glass rounded-3xl p-8 flex flex-col items-center text-center">
            <Avatar src={incoming.caller?.avatar} size={80} tier={incoming.caller?.tier} level={incoming.caller?.level} />
            <h2 className="text-white font-extrabold text-lg mt-4 font-display">{incoming.caller?.name}</h2>
            <p className="text-zinc-400 text-xs mt-1 flex items-center gap-1.5 justify-center">
              {incoming.mode === "video" ? <Video size={13} /> : <Mic size={13} />}
              Incoming {incoming.mode} call · <CoinIcon size={11} className="align-[-1px]" /> {incoming.rate}/min for caller
            </p>
            <div className="flex gap-4 mt-7">
              <button data-testid="reject-call-button" onClick={rejectIncoming}
                className="w-14 h-14 rounded-full bg-rose-600 flex items-center justify-center text-white active:scale-90 transition-transform">
                <PhoneOff size={22} />
              </button>
              <button data-testid="accept-call-button" onClick={acceptIncoming}
                className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center text-white active:scale-90 transition-transform pulse-live">
                <PhoneCall size={22} />
              </button>
            </div>
          </div>
        </div>
      )}

      {outgoing && (
        <div className="fixed inset-0 z-[11000] bg-black/85 flex items-center justify-center" data-testid="outgoing-call-modal">
          <div className="max-w-md w-full mx-6 glass rounded-3xl p-8 flex flex-col items-center text-center">
            <div className="pulse-live"><Avatar src={outgoing.callee?.avatar} size={80} tier={outgoing.callee?.tier} /></div>
            <h2 className="text-white font-extrabold text-lg mt-4 font-display">{outgoing.callee?.name}</h2>
            <p className="text-zinc-400 text-xs mt-1 flex items-center justify-center gap-1">Calling… <CoinIcon size={11} /> {outgoing.call.rate}/min</p>
            <button data-testid="cancel-call-button" onClick={cancelOutgoing}
              className="mt-7 w-14 h-14 rounded-full bg-rose-600 flex items-center justify-center text-white active:scale-90 transition-transform">
              <PhoneOff size={22} />
            </button>
          </div>
        </div>
      )}
      {pkChallenge && (
        <div className="fixed inset-0 z-[11000] bg-black/85 flex items-center justify-center" data-testid="pk-challenge-modal">
          <div className="max-w-md w-full mx-6 glass rounded-3xl p-8 flex flex-col items-center text-center">
            <div className="pulse-live"><Avatar src={pkChallenge.host_a?.avatar} size={80} /></div>
            <h2 className="text-white font-extrabold text-lg mt-4 font-display flex items-center gap-2">
              <Swords size={18} className="text-pink-400" /> PK Challenge!
            </h2>
            <p className="text-zinc-400 text-xs mt-1.5">
              <span className="text-white font-bold">{pkChallenge.host_a?.name}</span> challenges you to a {Math.round((pkChallenge.duration || 180) / 60)}-minute PK battle.
              Most gift coins wins! ⚔️
            </p>
            <div className="flex gap-4 mt-7">
              <button data-testid="pk-reject-button" onClick={rejectPk}
                className="px-6 py-3 rounded-full bg-white/10 text-zinc-300 text-xs font-bold active:scale-95 transition-transform">
                Decline
              </button>
              <button data-testid="pk-accept-button" onClick={acceptPk}
                className="px-6 py-3 rounded-full gradient-primary text-white text-xs font-extrabold shadow-[0_0_18px_rgba(236,72,153,0.6)] active:scale-95 transition-transform">
                ⚔️ Accept Battle
              </button>
            </div>
          </div>
        </div>
      )}
    </CallContext.Provider>
  );
}

export const useCall = () => useContext(CallContext);
